import { AxiosError } from 'axios';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { CreateJobPostDataType } from '../../../models/dashboard/jobs/createJobPost.model';
import {
  ErrorHttpResponse,
  HttpConfig,
  SuccessHttpResponse,
} from '../../../models/https';
import useHttps from '../../useHttps';
import { useAppDisPatch, useAppSelector } from '../../../redux/store';
import { jobCreatedListActions } from '../../../redux/slices/jobCreatedListSlice';
import useMiniLoad from '../dashboardLoading/useMiniLoad';
import { useNavigate } from 'react-router';
import { LOCUMJOBS, USERS_SETTINGS } from '../../../routes/routeConstants';
import { useContext } from 'react';
import { ModalContext } from '../../../context/ModalContext';
import showInteractiveToast from '../../../components/Modals/ToastModals/InteractiveToast';

const useCreateJobPost = () => {
  const { authData } = useAppSelector((state) => state.auth);
  const request = useHttps();
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDisPatch();

  const navigate = useNavigate();

  const modalContext = useContext(ModalContext);

  const { getMiniLoadData } = useMiniLoad();

  const onCreateJobPostError = (err: AxiosError<ErrorHttpResponse>) => {
    let errorMessage =
      err?.response?.data?.message ?? 'Something went wrong while creating job';

    errorMessage = errorMessage.replace(/endpoint/gi, 'action'); // 🔁 Replace 'endpoint' with 'action'

    if (errorMessage.toLowerCase().includes('access level')) {
      showInteractiveToast({
        message: errorMessage,
        onConfirm: () => {
          navigate(`/${USERS_SETTINGS}`);
          modalContext?.displayModal(false);
        },
      });
    } else {
      toast.error(errorMessage);
    }
  };

  const onCreateJobPostSuccess =
    (resetFormFn: (...args: any) => void) =>
    ({ data, message }: SuccessHttpResponse<any>) => {
      toast.success(message);
      // Automatically update the created jobs list with the new job data.
      dispatch(jobCreatedListActions.addJob(data));
      resetFormFn();
      getMiniLoadData();
      navigate(`/${LOCUMJOBS}`);
      modalContext?.displayModal(false);
    };

  const createJobPost = (
    data: CreateJobPostDataType,
    resetFormFn: (...args: any) => void
  ) => {
    if (authData?.status !== 'ACTIVE') {
      toast.error(
        `You are ${authData?.status} and can not perform this action`
      );
      return;
    }
    // Convert pay and yearsOfExperience to numbers
    const payload = {
      ...data,
      pay: Number(data.pay),
      yearsOfExperience: Number(data.yearsOfExperience),
    };

    const url: HttpConfig = {
      url: `jobs-service/jobs`,
      method: 'post',
      data: payload,
    };

    request(
      url,
      onCreateJobPostSuccess(resetFormFn),
      setLoading,
      onCreateJobPostError
    );
  };

  return { createJobPost, loading };
};

export default useCreateJobPost;
